import React, { useEffect, useMemo, useRef, useState } from 'react'
import { MicrophoneMute } from 'iconoir-react'
import AudioVisualizer from '../candidate/Chat/AudioVisualizerActive'
import {
  getParticipantInitials,
  getRealAvatarUrl,
} from '../utils/audioParticipantIdentity'

const ROLE_STYLES = {
  coach: {
    cardBg: 'bg-[#eaf5ec]',
    cardBgDark: 'bg-[#1a2e1f]',
    avatarBg: 'bg-[#1c6f29]',
    isCoachStream: true,
  },
  candidate: {
    cardBg: 'bg-[#e8f0f7]',
    cardBgDark: 'bg-[#152433]',
    avatarBg: 'bg-[#08467c]',
    isCoachStream: false,
  },
}

const AVATAR_SIZE_CLASS = 'h-16 w-16'
const MIN_VIZ_HEIGHT = 64
const MAX_VIZ_HEIGHT = 140
/** Keeps avatar block height stable with/without the "Muted" label */
const STATUS_SLOT_CLASS = 'mt-1 h-4'

/**
 * Audio participant card — centered avatar/initials + fluid modern visualizer.
 * Visualizer fills remaining card width/height (CTS-9778 candidate whitespace fix).
 */
const AudioParticipantCard = ({
  role = 'candidate',
  person = null,
  audioStream = null,
  isMuted = false,
  showVisualizer = true,
  isDarkMode = false,
  footer = null,
}) => {
  const styles = ROLE_STYLES[role] || ROLE_STYLES.candidate
  const photoUrl = useMemo(() => getRealAvatarUrl(person), [person])
  const initials = useMemo(() => getParticipantInitials(person), [person])
  const vizContainerRef = useRef(null)
  const [vizSize, setVizSize] = useState({ width: 280, height: MIN_VIZ_HEIGHT })

  const showPhoto = Boolean(photoUrl) && !isMuted

  const hideVisualizerForSafariMobile =
    typeof navigator !== 'undefined' &&
    navigator.userAgent.includes('Mobile') &&
    navigator.userAgent.includes('Safari')

  const canShowVisualizer =
    showVisualizer && !hideVisualizerForSafariMobile && !isMuted

  useEffect(() => {
    const el = vizContainerRef.current
    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined
    }

    const updateSize = () => {
      const width = Math.max(120, Math.floor(el.clientWidth))
      // Prefer container height; clamp so short/tall cards still look balanced
      const rawHeight = Math.floor(el.clientHeight)
      const height = Math.min(
        MAX_VIZ_HEIGHT,
        Math.max(MIN_VIZ_HEIGHT, rawHeight || MIN_VIZ_HEIGHT),
      )
      setVizSize((prev) =>
        prev.width === width && prev.height === height
          ? prev
          : { width, height },
      )
    }

    updateSize()
    const observer = new ResizeObserver(updateSize)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      className={`flex h-full min-h-0 w-full flex-col items-center rounded-2xl px-3 py-3 ${
        isDarkMode ? styles.cardBgDark : styles.cardBg
      }`}
    >
      <div className="relative flex flex-shrink-0 flex-col items-center">
        <div className={`relative ${AVATAR_SIZE_CLASS}`}>
          {showPhoto ? (
            <img
              src={photoUrl}
              alt=""
              className={`${AVATAR_SIZE_CLASS} rounded-full object-cover`}
            />
          ) : (
            <div
              className={`flex ${AVATAR_SIZE_CLASS} items-center justify-center rounded-full ${styles.avatarBg}`}
              aria-hidden="true"
            >
              <span className="font-roboto text-xl font-semibold text-white">
                {initials}
              </span>
            </div>
          )}

          {isMuted && (
            <span
              className="absolute -bottom-0.5 -right-0.5 flex h-7 w-7 items-center justify-center rounded-full bg-[#e11d48] text-white shadow-sm"
              aria-hidden="true"
            >
              <MicrophoneMute className="h-4 w-4" strokeWidth={2} />
            </span>
          )}
        </div>

        <div className={STATUS_SLOT_CLASS}>
          {isMuted ? (
            <p className="font-roboto text-xs leading-4 text-gray-500">Muted</p>
          ) : null}
        </div>
      </div>

      {/* Grows to fill leftover card space so candidate side-by-side cards use width/height */}
      <div
        ref={vizContainerRef}
        className="mt-2 flex min-h-[64px] w-full min-w-0 flex-1 items-center justify-center"
      >
        {canShowVisualizer ? (
          <AudioVisualizer
            audioStream={audioStream}
            isMuted={false}
            className="visualizer block h-full max-h-full w-full max-w-full"
            width={vizSize.width}
            height={vizSize.height}
            isCoachStream={styles.isCoachStream}
            showWhenMuted={false}
          />
        ) : null}
      </div>

      {footer ? <div className="mt-2 flex-shrink-0">{footer}</div> : null}
    </div>
  )
}

export default AudioParticipantCard
